/**
 * Skate chats — pure helpers shared by the store and pages.
 * Contract: docs/SKATE_CHAT.md. The rules enforce `senderRole`, so
 * `resolveChatSenderRole` must match `chatSenderRoleOk` in firestore.rules.
 */

export type ChatSenderRole = 'superAdmin' | 'cityAdmin' | 'member' | 'system'
export type ChatReportReason = 'spam' | 'harassment' | 'inappropriate' | 'other'

/** Firestore Timestamp-like value (or a Date / millis in tests). */
export type TimeLike = { toMillis: () => number } | Date | number | null | undefined

export interface SkateChat {
  id: string
  scheduleId: string
  cityId: string
  name: string
  dayOfWeek: number
  dayName: string
  venue: string
  paceLabel: string
  /** "10pm" / "10:30pm" */
  timeLabel: string
  sortKey: number
  /** inactive = skate deactivated (read-only until reactivated); archived = skate deleted. */
  status: 'active' | 'inactive' | 'archived'
  /** Admin lock: nobody can send while true. */
  locked: boolean
  lockedByName: string
  participantCount: number
  lastMessage: {
    text: string
    senderUid: string
    senderName: string
    senderRole: ChatSenderRole
    kind: 'text' | 'image' | 'system'
    deleted: boolean
    messageId?: string
  } | null
  lastMessageAt: TimeLike
}

export interface ChatParticipant {
  uid: string
  cityId: string
  displayName: string
  muted: boolean
  lastReadAt: TimeLike
  joinedAt?: TimeLike
  /** The server has confirmed this membership (a fresh join is local until then). */
  confirmed?: boolean
}

export interface ChatMessage {
  id: string
  kind: 'text' | 'image' | 'system'
  text: string
  image: { path: string; url: string; width: number; height: number } | null
  senderUid: string
  senderName: string
  senderRole: ChatSenderRole
  createdAt: TimeLike
  deleted: boolean
  deletedBy: string | null
  /** Set on the copy of a Super Admin broadcast. */
  broadcastId?: string | null
  broadcastScope?: 'all' | 'some' | null
  broadcastChatCount?: number | null
  /** Local write not yet acknowledged by the server. */
  pending?: boolean
}

export const CHAT_MESSAGE_MAX = 2000
export const CHAT_MESSAGE_COUNTER_FROM = 1800
/** Latest messages loaded when a chat opens. */
export const CHAT_INITIAL_PAGE_SIZE = 30
/** Older messages added each time the user scrolls to the top. */
export const CHAT_OLDER_PAGE_SIZE = 20
export const CHAT_RUN_GAP_MS = 5 * 60 * 1000
export const CHAT_REPORT_DETAILS_MAX = 500

/** "Broadcast to all Halifax chats" / "Broadcast to 3 Halifax chats" under a broadcast copy. */
export function broadcastCaption (message: Pick<ChatMessage, 'broadcastScope' | 'broadcastChatCount'>, cityLabel: string): string {
  const count = Number(message.broadcastChatCount) || 0
  if (message.broadcastScope !== 'some' || count <= 0) return `Broadcast to all ${cityLabel} chats`
  return `Broadcast to ${count} ${cityLabel} ${count === 1 ? 'chat' : 'chats'}`
}

/** Short city name for chat copy ("Halifax", "South Shore"). */
export function chatCityLabel (cityId: string, cityName?: string | null): string {
  if (cityId === 'southshore' || cityId === 'bridgewater') return 'South Shore'
  return (cityName || '').trim() || cityId.charAt(0).toUpperCase() + cityId.slice(1)
}

export const CHAT_REPORT_REASONS: Array<{ value: ChatReportReason; label: string }> = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'inappropriate', label: 'Inappropriate content' },
  { value: 'other', label: 'Other' }
]

export const CHAT_ROLE_LABELS: Record<'superAdmin' | 'cityAdmin', string> = {
  superAdmin: 'Super Admin',
  cityAdmin: 'City Admin'
}

const DAY_ABBREVIATIONS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const isTruthyBool = (v: unknown) => v === true || v === 'true'

export function toMillis (value: TimeLike): number | null {
  if (value == null) return null
  if (typeof value === 'number') return value
  if (value instanceof Date) return value.getTime()
  if (typeof (value as any).toMillis === 'function') return (value as any).toMillis()
  return null
}

export function isChatAdminRole (role: ChatSenderRole | undefined | null): role is 'superAdmin' | 'cityAdmin' {
  return role === 'superAdmin' || role === 'cityAdmin'
}

/** The exact role firestore.rules expects on a new message from this profile. */
export function resolveChatSenderRole (profile: any, chatCityId: string): Exclude<ChatSenderRole, 'system'> {
  if (isTruthyBool(profile?.isSuperAdmin)) return 'superAdmin'
  const cityKeys = chatCityId === 'southshore' ? ['southshore', 'bridgewater'] : [chatCityId]
  if (cityKeys.some((key) => isTruthyBool(profile?.cityData?.[key]?.isAdmin))) return 'cityAdmin'
  return 'member'
}

export function chatDayBadge (chat: Pick<SkateChat, 'dayOfWeek'>): string {
  return DAY_ABBREVIATIONS[chat.dayOfWeek] || ''
}

/** Only active, unlocked chats take new messages. */
export function isChatWritable (chat: Pick<SkateChat, 'status' | 'locked'>): boolean {
  return chat.status === 'active' && !chat.locked
}

export type ComposerState = 'archived' | 'inactive' | 'locked' | 'muted' | 'join' | 'adminJoin' | 'compose'

/** What sits where the composer goes. First match wins (docs/SKATE_CHAT.md). */
export function composerState (
  chat: Pick<SkateChat, 'status' | 'locked'>,
  { joined, isModerator, mutedHere }: { joined: boolean; isModerator: boolean; mutedHere: boolean }
): ComposerState {
  if (chat.status === 'archived') return 'archived'
  if (chat.status === 'inactive') return 'inactive'
  if (chat.locked) return 'locked'
  if (mutedHere) return 'muted'
  if (!joined) return isModerator ? 'adminJoin' : 'join'
  return 'compose'
}

/** Admins can mute members, never other admins or themselves. */
export function canMuteFromChatting (targetUid: string, targetRole: ChatSenderRole | undefined, myUid: string | null | undefined, isModerator: boolean): boolean {
  return isModerator && Boolean(targetUid) && targetUid !== myUid && !isChatAdminRole(targetRole) && targetRole !== 'system'
}

export function chatMuteId (cityId: string, uid: string): string {
  return `${cityId}_${uid}`
}

/** List row title: "Forum · Beginner" (the day and time live in the tile). */
export function chatRowTitle (chat: Pick<SkateChat, 'venue' | 'paceLabel' | 'name'>): string {
  const parts = [chat.venue, chat.paceLabel].map((p) => (p || '').trim()).filter(Boolean)
  return parts.length ? parts.join(' · ') : chat.name
}

/** Tile time split for stacking: "10:30pm" → { time: "10:30", meridiem: "PM" }. */
export function chatTileTime (chat: Pick<SkateChat, 'timeLabel'>): { time: string; meridiem: string } {
  const match = /^(\d{1,2}(?::\d{2})?)\s*([ap]m)?$/i.exec((chat.timeLabel || '').trim())
  if (!match) return { time: (chat.timeLabel || '').toUpperCase(), meridiem: '' }
  return { time: match[1], meridiem: (match[2] || '').toUpperCase() }
}

export function chatMembersLabel (count: number): string {
  if (count <= 0) return 'Be the first to join'
  return `${count} ${count === 1 ? 'member' : 'members'}`
}

export function hasUnread (chat: SkateChat, participant: ChatParticipant | undefined, myUid: string | null | undefined): boolean {
  if (!participant || !chat.lastMessage) return false
  if (chat.lastMessage.senderUid === myUid) return false
  const last = toMillis(chat.lastMessageAt)
  if (last == null) return false
  const read = toMillis(participant.lastReadAt)
  return read == null || last > read
}

/** "Sam: see you there" / "Sam sent a photo" / "Sam: Message deleted" ("You: …" for mine). */
export function chatPreviewText (chat: SkateChat, myUid?: string | null): string {
  const last = chat.lastMessage
  if (!last) return 'No messages yet'
  if (last.kind === 'system') return last.text
  const who = last.senderUid === myUid ? 'You' : (last.senderName || 'Someone')
  if (last.deleted) return `${who}: Message deleted`
  if (last.kind === 'image' && !last.text.trim()) return `${who} sent a photo`
  if (last.kind === 'image') return `${who} sent a photo: ${last.text}`
  return `${who}: ${last.text}`
}

export interface ChatListSections {
  joined: SkateChat[]
  available: SkateChat[]
  past: SkateChat[]
}

/**
 * Your chats (joined + active/inactive, newest activity first), Available to join
 * (active, Regular Status skates first, then by day/time), Past chats (joined + archived).
 */
export function sectionChats (
  chats: SkateChat[],
  participants: Record<string, ChatParticipant>,
  isRegular: (scheduleId: string) => boolean = () => false
): ChatListSections {
  const joined: SkateChat[] = []
  const available: SkateChat[] = []
  const past: SkateChat[] = []
  for (const chat of chats) {
    const isMember = Boolean(participants[chat.id])
    if (chat.status === 'archived') {
      if (isMember) past.push(chat)
    } else if (isMember) {
      joined.push(chat)
    } else if (chat.status === 'active') {
      available.push(chat)
    }
  }
  const byActivity = (a: SkateChat, b: SkateChat) =>
    (toMillis(b.lastMessageAt) ?? 0) - (toMillis(a.lastMessageAt) ?? 0) || a.sortKey - b.sortKey
  joined.sort(byActivity)
  past.sort(byActivity)
  available.sort((a, b) =>
    Number(isRegular(b.scheduleId)) - Number(isRegular(a.scheduleId)) || a.sortKey - b.sortKey)
  return { joined, available, past }
}

/** Joined chats (not archived) with unread messages (the menu badge). */
export function countUnreadChats (
  chats: SkateChat[],
  participants: Record<string, ChatParticipant>,
  myUid: string | null | undefined
): number {
  return chats.filter((c) => c.status !== 'archived' && hasUnread(c, participants[c.id], myUid)).length
}

function startOfDay (ms: number): number {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** "Today" / "Yesterday" / "Mon, Oct 6" / "Mon, Oct 6, 2025". */
export function formatChatDayLabel (ms: number, now: Date = new Date()): string {
  const today = startOfDay(now.getTime())
  const day = startOfDay(ms)
  if (day === today) return 'Today'
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  if (day === yesterday.getTime()) return 'Yesterday'
  const date = new Date(ms)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {})
  })
}

export function formatChatTime (ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

/** Short relative stamp for the list: "10:42 PM", "Yesterday", "Mon", "Oct 6". */
export function formatChatListStamp (ms: number | null, now: Date = new Date()): string {
  if (ms == null) return ''
  const today = startOfDay(now.getTime())
  const day = startOfDay(ms)
  const daysAgo = Math.round((today - day) / 86400000)
  if (daysAgo <= 0) return formatChatTime(ms)
  if (daysAgo === 1) return 'Yesterday'
  if (daysAgo < 7) return new Date(ms).toLocaleDateString('en-US', { weekday: 'short' })
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export type ChatTimelineItem =
  | { type: 'day'; key: string; label: string }
  | {
    type: 'message'
    key: string
    message: ChatMessage
    /** First bubble of a run from one sender: show the name. */
    startsRun: boolean
    endsRun: boolean
    timeMs: number
  }

/**
 * Oldest-first messages → timeline rows with day separators and sender runs.
 * A run breaks on a new sender, a new day, a system message, or a 5-minute gap.
 * Pending messages (no server time yet) use `nowMs`.
 */
export function buildChatTimeline (messages: ChatMessage[], now: Date = new Date()): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = []
  let lastDay: number | null = null
  let prev: { message: ChatMessage; timeMs: number } | null = null
  let prevItem: Extract<ChatTimelineItem, { type: 'message' }> | null = null

  for (const message of messages) {
    const timeMs = toMillis(message.createdAt) ?? now.getTime()
    const day = startOfDay(timeMs)
    const newDay = day !== lastDay
    if (newDay) {
      items.push({ type: 'day', key: `day-${day}`, label: formatChatDayLabel(timeMs, now) })
      lastDay = day
    }
    const startsRun = newDay ||
      !prev ||
      message.kind === 'system' ||
      prev.message.kind === 'system' ||
      prev.message.senderUid !== message.senderUid ||
      timeMs - prev.timeMs > CHAT_RUN_GAP_MS
    if (startsRun && prevItem) prevItem.endsRun = true
    const item = { type: 'message' as const, key: message.id, message, startsRun, endsRun: false, timeMs }
    items.push(item)
    prev = { message, timeMs }
    prevItem = item
  }
  if (prevItem) prevItem.endsRun = true
  return items
}

/** Members' messages from people I blocked are hidden; admins can't be blocked. */
export function isHiddenByBlock (message: ChatMessage, blockedUids: Set<string> | Record<string, unknown>): boolean {
  if (message.senderRole !== 'member') return false
  return blockedUids instanceof Set
    ? blockedUids.has(message.senderUid)
    : Boolean(blockedUids[message.senderUid])
}

/** Placeholder for a tombstone. The sender's name still shows above it (see the conversation). */
export function deletedMessageLabel (message: ChatMessage, myUid?: string | null): string {
  if (message.deletedBy && message.deletedBy !== message.senderUid) return 'Removed by an admin'
  return message.senderUid === myUid ? 'You deleted this message' : 'This message was deleted'
}

export function reportDocId (messageId: string, reporterUid: string): string {
  return `${messageId}_${reporterUid}`
}

/** Fit within a long edge (images are downscaled before upload). */
export function fitWithin (width: number, height: number, maxEdge = 1600): { width: number; height: number } {
  const longest = Math.max(width, height)
  if (longest <= maxEdge || longest === 0) return { width, height }
  const scale = maxEdge / longest
  return { width: Math.round(width * scale), height: Math.round(height * scale) }
}
